import { useState } from 'react';
import { AFFILIATE_URL, getAffiliateLinkProps } from '../../../constants/affiliate';

function SectionTitle({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="section-title text-slate-900 text-xl md:text-2xl lg:text-3xl font-bold mb-5 flex items-center gap-3">
      <span className="w-1 h-8 rounded-full bg-gradient-to-b from-blue-500 to-blue-400 flex-shrink-0"></span>
      <strong>{children}</strong>
    </h2>
  );
}

function SubTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-slate-800 font-bold text-sm md:text-base mb-3 flex items-center gap-2">
      <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
      {children}
    </h3>
  );
}

function BulletList({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="flex flex-col gap-1.5 pl-4">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

function CheckList({ items }: { items: { label: string; desc: string }[] }) {
  return (
    <ul className="flex flex-col gap-2 mb-5">
      {items.map((item) => (
        <li key={item.label} className="flex gap-2">
          <span className="text-blue-500 flex-shrink-0 mt-0.5"><i className="ri-check-line"></i></span>
          <span><strong className="text-slate-700">{item.label}:</strong> {item.desc}</span>
        </li>
      ))}
    </ul>
  );
}

function CTAButton({ href, label, source }: { href: string; label: string; source: string }) {
  return (
    <div className="flex justify-center mt-4">
      <a href={href} {...getAffiliateLinkProps(source, label)} className="group btn-premium text-white text-sm font-bold px-6 py-2.5 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer">
        <span className="relative z-10 flex items-center gap-2">
          {label}
          <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
        </span>
      </a>
    </div>
  );
}

/** Unified photo shell: rounded corners; frame hugs image (no letterboxing bands) */
const PHOTO_FRAME =
  'rounded-2xl overflow-hidden ring-1 ring-blue-200/45 shadow-sm';

function ContentImage({
  src,
  alt,
  href,
  btnLabel,
  source,
  imgClassName,
  frameClassName,
  mobileTall,
}: {
  src: string;
  alt: string;
  href: string;
  btnLabel: string;
  source: string;
  imgClassName?: string;
  frameClassName?: string;
  /** Portrait promos (bonuses / register / app): max height cap, intrinsic ratio */
  mobileTall?: boolean;
}) {
  const outerClasses = mobileTall
    ? 'mx-auto w-full max-w-[320px] shrink-0 lg:mx-0 lg:w-[280px] lg:max-w-[280px]'
    : 'mx-auto w-full max-w-[360px] shrink-0 lg:mx-0 lg:w-[300px] lg:max-w-[300px]';

  const frameExtra = frameClassName ?? '';

  return (
    <div className={outerClasses}>
      {mobileTall ? (
        <div className="flex w-full justify-center">
          <div className={`${PHOTO_FRAME} inline-block max-w-full ${frameExtra}`}>
            <img
              alt={alt}
              src={src}
              loading="lazy"
              decoding="async"
              className={`block h-auto max-h-[min(560px,80vh)] w-auto max-w-full ${imgClassName ?? ''}`}
            />
          </div>
        </div>
      ) : (
        <div className={`relative w-full ${PHOTO_FRAME} ${frameExtra}`}>
          <img
            alt={alt}
            src={src}
            loading="lazy"
            decoding="async"
            className={`block h-auto w-full ${imgClassName ?? ''}`}
          />
        </div>
      )}
      <CTAButton href={href} label={btnLabel} source={source} />
    </div>
  );
}

const faqItems = [
  { q: 'Is iPAY9 legal in Australia?', a: 'iPAY9 operates under a valid international e-gaming license (PAGCOR / Curacao), making it accessible to Australian users. We comply with all applicable international gaming regulations and provide a safe, secure entertainment environment.' },
  { q: 'What is the minimum deposit at iPAY9?', a: 'The minimum deposit at iPAY9 is AUD 10. We support Visa, Mastercard, PayID, Osko, POLi, Bank Transfer, Amopay, and USDT for fast and secure deposits.' },
  { q: 'What is the minimum withdrawal at iPAY9?', a: 'The minimum withdrawal at iPAY9 is AUD 50. Most withdrawals are processed within 5 minutes or instant. PayID, Osko, POLi or bank transfers are typically instant, while crypto withdrawals take around 10 minutes.' },
  { q: 'Do I need a VPN to access iPAY9?', a: 'No VPN is required. iPAY9 official website uses intelligent mirroring technology to ensure direct, fast access from anywhere in Australia without any additional software.' },
  { q: 'What games are available on iPAY9?', a: 'iPAY9 offers a wide range of entertainment including slots, live casino, sports betting, fishing games, card games, and Plinko. Our library is powered by top providers like BNG, JILI, VPOWER, Live22, Rich Gaming, CQ9, VPlus, ACE333, Pragmatic Play, and Evolution Gaming.' },
  { q: 'How do I download the iPAY9 app?', a: 'Android users can download the iPAY9 APK directly from our official website. iOS users can access iPAY9 via Safari browser and add it to their home screen for a native app-like experience.' },
];

export default function MainContent() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="flex-1 min-w-0">
      <div className="flex flex-col gap-12 text-slate-500 text-sm leading-relaxed">

        {/* About */}
        <div>
          <SectionTitle id="1">About IPAY9 Online Casino Australia</SectionTitle>
          <p className="mb-6">
            IPAY9 Online Casino Australia has established itself as a premier destination for local enthusiasts seeking a premium gambling experience. As the IPAY9 official website, we prioritize localized services, offering a seamless interface tailored specifically for the Australian market.
          </p>
          <p className="mb-6 rounded-xl border border-blue-200/60 bg-blue-50/50 px-4 py-3 text-slate-600">
            <strong className="text-slate-800">Trusted Pokies Australia (TPA):</strong>{' '}
            iPAY9 is trusted by TPA (Trusted Pokies Australia)—an Australian-facing pokies and casino information hub that highlights operators focused on transparent promotions, reliable payouts, and player support.
          </p>
          <div className="flex flex-col lg:flex-row gap-6 items-center mb-6">
            <ContentImage
              src="https://static.readdy.ai/image/10eea1721ed83e786f1369ea5cd4ade4/2a44460ca511fbb945b3321ead6b7ea5.png"
              alt="About IPAY9 Casino Australia"
              href={AFFILIATE_URL}
              btnLabel="Join us"
              source="main_about_join"
            />
            <div className="flex-1">
              <SubTitle>IPAY9 for Australian Players</SubTitle>
              <CheckList items={[
                { label: 'Localized Currency', desc: 'All transactions are handled in AUD, eliminating exchange fee headaches.' },
                { label: 'Cultural Content', desc: 'From popular local slot themes to regional sports betting markets.' },
                { label: 'Language Support', desc: 'Our interface is optimized for English speakers.' },
                { label: 'Stability', desc: 'High-uptime servers ensure that IPAY9 Casino remains accessible 24/7 without lag.' },
                { label: 'Exclusive Tech', desc: 'Use of the latest HTML5 technology ensures compatibility across all Australian mobile networks.' },
              ]} />
              <SubTitle>License &amp; Safety IPAY9 Australia</SubTitle>
              <p>Security is the cornerstone of IPAY9. We operate under a strict international e-gaming license, ensuring that every spin and bet on IPAY9 official website is fair and audited. We utilize 256-bit SSL encryption to protect your personal and financial data.</p>
            </div>
          </div>
        </div>

        {/* Top Games */}
        <div>
          <SectionTitle id="2">Top Games IPAY9 Casino Australia</SectionTitle>
          <p className="mb-5">The gaming portfolio at IPAY9 Casino Australia is curated to provide maximum variety and high RTP percentages. Our library is updated weekly, ensuring that the "New Games" section always features the trendiest titles.</p>
          <div className="glass-card rounded-xl p-5 md:p-6 mb-5">
            <SubTitle>Slots</SubTitle>
            <BulletList items={[
              <><strong className="text-slate-700">Top Providers:</strong> BNG, JILI, VPOWER, Pragmatic Play, SpadeGaming, and Live22.</>,
              <><strong className="text-slate-700">Popular Themes:</strong> Kraken Queen, Magic Apple, Hot Chili, Gate of Olympus, Roma, and Sahara Gold.</>,
              <><strong className="text-slate-700">Features:</strong> Free spins, cascading reels, and &quot;Buy Bonus&quot; options.</>,
              <><strong className="text-slate-700">Accessibility:</strong> All slots at IPAY9 Casino are optimized for mobile play.</>,
              <><strong className="text-slate-700">Demo Mode:</strong> Try most games for free before wagering real AUD.</>,
            ]} />
          </div>
          <SubTitle>Jackpots</SubTitle>
          <div className="overflow-x-auto mb-5">
            <table className="w-full border-collapse text-xs md:text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-blue-600/10 to-blue-400/5">
                  <th className="border border-blue-200/30 px-3 py-2.5 text-left text-slate-800 font-semibold">Jackpot Type</th>
                  <th className="border border-blue-200/30 px-3 py-2.5 text-left text-slate-800 font-semibold">Description</th>
                  <th className="border border-blue-200/30 px-3 py-2.5 text-left text-slate-800 font-semibold">Top Games</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { type: 'Progressive', desc: 'Prize pools that grow with every bet placed across the network.', games: 'Mega Moolah, Wolf Gold' },
                  { type: 'Fixed', desc: 'Set amounts like Mini, Minor, Major, and Grand.', games: "Dragon's Luck, 88 Fortunes" },
                  { type: 'Daily Drops', desc: 'Must-win jackpots that trigger every 24 hours.', games: 'Pragmatic Play Series' },
                  { type: 'Local Jackpots', desc: 'Exclusive pools only for IPAY9 Australia players.', games: 'Custom IPAY9 Titles' },
                ].map((row) => (
                  <tr key={row.type} className="border-b border-blue-100/30 hover:bg-blue-50/30 transition-colors">
                    <td className="border border-blue-200/30 px-3 py-2 text-slate-800 font-semibold">{row.type}</td>
                    <td className="border border-blue-200/30 px-3 py-2">{row.desc}</td>
                    <td className="border border-blue-200/30 px-3 py-2">{row.games}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col lg:flex-row gap-6 items-start mb-5">
            <div className="flex-1">
              <SubTitle>Live Casino</SubTitle>
              <BulletList items={[
                <><strong className="text-slate-700">Real Dealers:</strong> Interact with professional human dealers via HD streams.</>,
                <><strong className="text-slate-700">Game Variety:</strong> Baccarat, Roulette, Sic Bo, and Dragon Tiger.</>,
                <><strong className="text-slate-700">Evolution Gaming:</strong> Access Crazy Time and Monopoly Live.</>,
                <><strong className="text-slate-700">Betting Limits:</strong> Tables ranging from AUD 1 to VIP high-roller limits.</>,
                <><strong className="text-slate-700">Multiview:</strong> Watch multiple tables at once at IPAY9 Casino.</>,
              ]} />
              <div className="mt-5">
                <SubTitle>Fishing Games</SubTitle>
                <BulletList items={[
                  <><strong className="text-slate-700">Fishing Games:</strong> Shoot for big wins in Golden Fish Carnival and Boom Legend.</>,
                  <><strong className="text-slate-700">Crash Games:</strong> Test your nerves with Happy Fishing or Ocean King.</>,
                  <><strong className="text-slate-700">Instant Wins:</strong> Perfect for quick gaming sessions during a commute.</>,
                  <><strong className="text-slate-700">Social Play:</strong> See other players&apos; bets and wins in real-time.</>,
                  <><strong className="text-slate-700">Leaderboards:</strong> Compete for top spots in daily fishing tournaments.</>,
                ]} />
              </div>
            </div>
            <ContentImage
              src="https://static.readdy.ai/image/b513b5b4a847779ee65de1525456761b/0f2b19ee07e6225d1206a9c0459c7a53.png"
              alt="Live Casino"
              href={AFFILIATE_URL}
              btnLabel="Play casino"
              source="main_live_casino_play"
            />
          </div>
          <SubTitle>Table Games</SubTitle>
          <BulletList items={[
            <><strong className="text-slate-700">Live Dealers:</strong> Stream real-dealer tables in HD for a true casino feel.</>,
            <><strong className="text-slate-700">Baccarat:</strong> Classic banker/player action with fast rounds and flexible limits.</>,
            <><strong className="text-slate-700">Dragon Tiger:</strong> Simple two-hand format with quick outcomes.</>,
            <><strong className="text-slate-700">Roulette:</strong> Spin-based table play with multiple betting options.</>,
            <><strong className="text-slate-700">More Tables:</strong> Enjoy additional live table choices like Sic Bo depending on provider availability.</>,
          ]} />
        </div>

        {/* Sportsbook */}
        <div>
          <SectionTitle id="3">Sportsbook Australia IPAY9 Casino</SectionTitle>
          <p className="mb-6">
            Want football bet action in Australia? iPAY9 Sportsbook brings real-time live odds, fast bet placement, and in-play markets so you can follow every minute — from kick-off to full time.
          </p>
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            <ContentImage
              src="/sportsbook-football-bet.png"
              alt="Sportsbook football betting Australia IPAY9 Casino"
              href={AFFILIATE_URL}
              btnLabel="Bet now"
              source="main_sportsbook_bet"
            />
            <div className="flex-1">
              <SubTitle>Sportsbook Football Bet Markets</SubTitle>
              <BulletList items={[
                <><strong className="text-slate-700">Pre-match &amp; In-play:</strong> Place football bets before kick-off or switch to live sportsbook betting with dynamic odds.</>,
                <><strong className="text-slate-700">Main lines:</strong> 1X2, Draw No Bet, Double Chance, and Asian Handicap.</>,
                <><strong className="text-slate-700">Goals:</strong> Over/Under totals, Both Teams To Score, and team totals.</>,
                <><strong className="text-slate-700">Props:</strong> Correct Score, First/Anytime Goalscorer, corners, and cards (market availability varies).</>,
                <><strong className="text-slate-700">Outrights:</strong> League winner, top-four finishes, and top scorer futures.</>,
              ]} />
              <div className="mt-5">
                <SubTitle>Fast, Secure Sportsbook Experience</SubTitle>
                <BulletList items={[
                  <><strong className="text-slate-700">Real-time live odds:</strong> Odds refresh quickly so you can react to match momentum.</>,
                  <><strong className="text-slate-700">Mobile-friendly:</strong> Smooth sportsbook flow on mobile web and app.</>,
                  <><strong className="text-slate-700">Responsible play:</strong> Use limits and self-control tools when needed.</>,
                ]} />
              </div>
            </div>
          </div>
        </div>

        {/* Football bet — SEO guide */}
        <div>
          <SectionTitle id="football-bet">Football Betting Guide — Australia &amp; iPAY9 Sportsbook</SectionTitle>
          <p className="mb-5">
            Football is one of the most popular sports to bet on in Australia—from A-League and Socceroos fixtures to European leagues and international friendlies. On iPAY9 Sportsbook Australia, you can access pre-match and live football bet markets priced in AUD.
          </p>
          <SubTitle>Why Football Betting Matters for Australian Fans</SubTitle>
          <p className="mb-5">
            Australians follow domestic and international football closely, from local leagues to major European competitions. A dedicated football betting guide helps you compare match odds, outright futures, and in-play sportsbook lines without missing kick-off.
          </p>
          <SubTitle>Football Outrights &amp; Futures</SubTitle>
          <BulletList items={[
            <><strong className="text-slate-700">League winner:</strong> Back a club to win the title before or during the season.</>,
            <><strong className="text-slate-700">Season markets:</strong> Top-four finishes, relegation, and qualification scenarios.</>,
            <><strong className="text-slate-700">Player awards:</strong> Top scorer and player-performance markets where offered.</>,
            <><strong className="text-slate-700">Match specials:</strong> Team totals, clean sheets, and time-bracket goals—availability varies by fixture.</>,
          ]} />
          <div className="mt-5">
            <SubTitle>Live Football Odds &amp; In-Play Sportsbook Markets</SubTitle>
            <BulletList items={[
              <><strong className="text-slate-700">Match winner &amp; totals:</strong> React after kick-off as momentum shifts from defence to attack.</>,
              <><strong className="text-slate-700">Next goal &amp; corners:</strong> Short-cycle markets suited to fast-moving football matches.</>,
              <><strong className="text-slate-700">Cash-out style tools:</strong> Where supported, manage exposure before the final whistle.</>,
              <><strong className="text-slate-700">Mobile-first:</strong> Follow live football betting from phone or tablet on the iPAY9 sportsbook app or mobile site.</>,
            ]} />
          </div>
          <div className="mt-5">
            <SubTitle>International vs Club Football Betting</SubTitle>
            <p className="mb-4">
              International squads have shorter preparation windows than club teams, which can affect form and squad depth. Football bet markets often price emotion and rivalry differently from weekly league odds—use stats, injuries, and scheduling when you evaluate prices on iPAY9.
            </p>
          </div>
          <SubTitle>Responsible Sportsbook Wagering</SubTitle>
          <p className="mb-4">
            High-profile fixtures can encourage longer sessions and impulse bets. Set a budget for football betting, use deposit limits where available, and treat promotions as extras—not guarantees of profit.
          </p>
          <CTAButton href={AFFILIATE_URL} label="Bet Football on iPAY9 Sportsbook" source="main_football_bet" />
        </div>

        {/* Bonuses */}
        <div>
          <SectionTitle id="4">Bonuses Australia IPAY9 Casino</SectionTitle>
          <p className="mb-6">
            Promotions are a big part of the iPAY9 Online Casino Australia experience. Bonus availability can change by time and account status, so always check the latest promo rules inside your iPAY9 account.
          </p>
          <div className="flex flex-col lg:flex-row gap-6 items-start mb-6">
            <div className="flex-1">
              <SubTitle>Daily Deposit Bonus (Deposit AUD 100 → Up to AUD 200)</SubTitle>
              <BulletList items={[
                <><strong className="text-slate-700">How it works:</strong> Deposit AUD 100 to unlock daily reward eligibility.</>,
                <><strong className="text-slate-700">Rewards:</strong> Daily rewards up to AUD 200 (promo rules apply).</>,
                <><strong className="text-slate-700">Claim:</strong> Check the Promo section inside your iPAY9 account.</>,
                <><strong className="text-slate-700">AUD Wallet:</strong> Designed for Australian players using AUD deposits.</>,
              ]} />
              <div className="mt-5">
                <SubTitle>Every Deposit Random Bonus</SubTitle>
                <BulletList items={[
                  <><strong className="text-slate-700">Instant alerts:</strong> Get notified about random bonus drops on deposits.</>,
                  <><strong className="text-slate-700">Fast to claim:</strong> Usually available directly in your promo area.</>,
                  <><strong className="text-slate-700">Best for regular play:</strong> Designed to reward consistent deposit activity.</>,
                ]} />
              </div>
            </div>
            <ContentImage
              src="https://static.readdy.ai/image/b513b5b4a847779ee65de1525456761b/0ca57c161bc37b71c2503b89d3d15646.png"
              alt="Bonuses Australia IPAY9 Casino"
              href={AFFILIATE_URL}
              btnLabel="Claim bonuses"
              source="main_bonuses_claim"
              mobileTall
            />
          </div>
          <div className="flex flex-col lg:flex-row gap-6 items-start mb-5">
            <ContentImage
              src="https://static.readdy.ai/image/b513b5b4a847779ee65de1525456761b/3985a89e3e8dd3f036191566f87a2086.png"
              alt="Cashback Australia"
              href={AFFILIATE_URL}
              btnLabel="Get Cashback"
              source="main_cashback"
              mobileTall
            />
            <div className="flex-1">
              <SubTitle>Weekly Rebate (Up to 5%)</SubTitle>
              <BulletList items={[
                <><strong className="text-slate-700">Auto credit:</strong> Weekly rebate credited automatically (rules apply).</>,
                <><strong className="text-slate-700">Rate:</strong> Weekly rebate up to 5% for eligible play.</>,
                <><strong className="text-slate-700">Trackable:</strong> View your transaction history and rebate status in-app.</>,
              ]} />
              <div className="mt-5">
                <SubTitle>Share &amp; Earn (Weekly 7% Commission)</SubTitle>
                <BulletList items={[
                  <><strong className="text-slate-700">Referral link:</strong> Generate your share link inside iPAY9.</>,
                  <><strong className="text-slate-700">Commission:</strong> Weekly commission up to 7% (network rules apply).</>,
                  <><strong className="text-slate-700">Real-time tracking:</strong> Monitor your network performance and payouts.</>,
                ]} />
              </div>
            </div>
          </div>
          <SubTitle>Monthly Top 10 Deposit Leaderboard (Up to AUD 1999)</SubTitle>
          <BulletList items={[
            <><strong className="text-slate-700">Rankings:</strong> Real-time deposit rankings throughout the month.</>,
            <><strong className="text-slate-700">Prizes:</strong> Rewards up to AUD 1999 for top depositors.</>,
            <><strong className="text-slate-700">VIP tech tiers:</strong> Frequent play can unlock higher tier perks and benefits.</>,
          ]} />
        </div>

        {/* VIP Club */}
        <div>
          <SectionTitle id="5">VIP Club IPAY9</SectionTitle>
          <p className="mb-6">The IPAY9 VIP Club is an elite tier for our most dedicated players. As you climb the ranks at IPAY9 Casino, you unlock a world of personalized service and exclusive financial perks.</p>
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            <div className="flex-1">
              <SubTitle>Levels at IPAY9 Casino</SubTitle>
              <BulletList items={[
                <><strong className="text-slate-700">Bronze:</strong> Entry-level tier with basic rebate increases and birthday gifts.</>,
                <><strong className="text-slate-700">Silver:</strong> Unlocks higher daily withdrawal limits and monthly loyalty bonuses.</>,
                <><strong className="text-slate-700">Gold:</strong> Includes a personal account manager and exclusive event invitations.</>,
                <><strong className="text-slate-700">Platinum:</strong> Premium rebates (up to 1.2%) and customized promotional offers.</>,
                <><strong className="text-slate-700">Diamond:</strong> The pinnacle of IPAY9 status, featuring invitations to luxury trips.</>,
              ]} />
              <div className="mt-5">
                <SubTitle>VIP Withdrawals IPAY9 Australia</SubTitle>
                <BulletList items={[
                  <><strong className="text-slate-700">Priority Processing:</strong> VIP withdrawals are moved to the front of the queue.</>,
                  <><strong className="text-slate-700">Higher Limits:</strong> Daily limits can extend up to AUD 200,000 for top tiers.</>,
                  <><strong className="text-slate-700">Fewer Restrictions:</strong> Reduced turnover requirements on certain loyalty bonuses.</>,
                  <><strong className="text-slate-700">Dedicated Support:</strong> A direct line to the VIP finance team for instant assistance.</>,
                  <><strong className="text-slate-700">Gift Redemption:</strong> Exchange VIP points for high-end gadgets or luxury items.</>,
                ]} />
              </div>
            </div>
            <ContentImage
              src="https://static.readdy.ai/image/b513b5b4a847779ee65de1525456761b/46b7b8a12df5e8b3a0766d36d2ea1227.png"
              alt="VIP Club IPAY9"
              href={AFFILIATE_URL}
              btnLabel="VIP players"
              source="main_vip_players"
            />
          </div>
        </div>

        {/* AUD Payments */}
        <div>
          <SectionTitle id="6">AUD Payments IPAY9 Casino Australia</SectionTitle>
          <p className="mb-5">Convenience is key, which is why IPAY9 Casino Australia supports the most popular local payment methods. By integrating with major Australian banks and e-wallets, IPAY9 AU offers near-instant transactions.</p>
          <SubTitle>Deposits IPAY9 AU</SubTitle>
          <BulletList items={[
            <><strong className="text-slate-700">E-Wallets:</strong> PayID & Osko.</>,
            <><strong className="text-slate-700">Bank Transfers:</strong> Direct bank transfer via major Australian banks including ANZ, CBA, NAB, and Westpac.</>,
            <><strong className="text-slate-700">Crypto:</strong> Secure USDT (TRC20) deposits for privacy and no limits.</>,
            <><strong className="text-slate-700">Minimums:</strong> Start playing with as little as AUD 10.</>,
            <><strong className="text-slate-700">Security:</strong> All transactions are protected by bank-grade encryption.</>,
          ]} />
          <div className="mt-5">
            <SubTitle>Withdrawals IPAY9 Australia</SubTitle>
            <div className="overflow-x-auto mb-5">
              <table className="w-full border-collapse text-xs md:text-sm">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-600/10 to-blue-400/5">
                    <th className="border border-blue-200/30 px-3 py-2.5 text-left text-slate-800 font-semibold">Method</th>
                    <th className="border border-blue-200/30 px-3 py-2.5 text-left text-slate-800 font-semibold">Min Deposit</th>
                    <th className="border border-blue-200/30 px-3 py-2.5 text-left text-slate-800 font-semibold">Max Deposit</th>
                    <th className="border border-blue-200/30 px-3 py-2.5 text-left text-slate-800 font-semibold">Min Withdraw</th>
                    <th className="border border-blue-200/30 px-3 py-2.5 text-left text-slate-800 font-semibold">Processing</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { method: 'PayID/Osko', minDep: 'AUD 10', maxDep: 'AUD 30,000', minWith: 'AUD 50', proc: 'Instant' },
                    { method: 'POLi/Bank Transfer', minDep: 'AUD 30', maxDep: 'AUD 50,000', minWith: 'AUD 50', proc: 'Instant' },
                    { method: 'USDT (TRC20)', minDep: 'AUD 50', maxDep: 'No limit', minWith: 'AUD 100', proc: '5-15 min' },
                  ].map((row) => (
                    <tr key={row.method} className="border-b border-blue-100/30 hover:bg-blue-50/30 transition-colors">
                      <td className="border border-blue-200/30 px-3 py-2 text-slate-800 font-semibold">{row.method}</td>
                      <td className="border border-blue-200/30 px-3 py-2">{row.minDep}</td>
                      <td className="border border-blue-200/30 px-3 py-2">{row.maxDep}</td>
                      <td className="border border-blue-200/30 px-3 py-2">{row.minWith}</td>
                      <td className="border border-blue-200/30 px-3 py-2 text-blue-600 font-semibold">{row.proc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <SubTitle>No VPN Needed IPAY9 Casino AU</SubTitle>
          <p>Unlike many other platforms, the IPAY9 official website is optimized for direct access within Australia. We use intelligent mirroring technology to ensure that players do not need to use a VPN to enjoy their favorite games.</p>
        </div>

        {/* Register */}
        <div>
          <SectionTitle id="7">Register IPAY9 Casino Australia</SectionTitle>
          <p className="mb-6">Joining the IPAY9 Casino Australia family takes less than a minute. Our registration process is streamlined to be user-friendly, requiring only essential information to get you started.</p>
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            <ContentImage
              src="/ipay9-register-page.png"
              alt="Register IPAY9 Casino Australia"
              href={AFFILIATE_URL}
              btnLabel="Register now"
              source="main_register_now"
              mobileTall
            />
            <div className="flex-1">
              <SubTitle>Quick Signup IPAY9 AU</SubTitle>
              <ol className="flex flex-col gap-1.5 mb-5 pl-4 list-decimal">
                <li><strong className="text-slate-700">Visit Site:</strong> Click the &quot;Register&quot; button on the IPAY9 official website.</li>
                <li><strong className="text-slate-700">Provide Details:</strong> Select the AUD wallet and enter your phone number or email address.</li>
                <li><strong className="text-slate-700">First Deposit:</strong> Deposit a minimum of AUD 30 via your preferred local bank.</li>
                <li><strong className="text-slate-700">Claim Bonus:</strong> Your welcome bonus will be automatically credited to your balance.</li>
                <li><strong className="text-slate-700">Start Playing:</strong> Browse the library and place your first bet!</li>
              </ol>
              <SubTitle>Login IPAY9 Australia</SubTitle>
              <BulletList items={[
                <><strong className="text-slate-700">Remember Me:</strong> Use the secure &quot;Remember Me&quot; feature for faster future logins.</>,
                <><strong className="text-slate-700">Two-Factor Authentication:</strong> Optional 2FA for enhanced account security.</>,
                <><strong className="text-slate-700">Password Recovery:</strong> Quick SMS recovery if you forget your details.</>,
                <><strong className="text-slate-700">Device Management:</strong> View and manage which devices are logged into your IPAY9 account.</>,
                <><strong className="text-slate-700">Cross-Platform:</strong> The same login works on desktop, mobile web, and the app.</>,
              ]} />
            </div>
          </div>
        </div>

        {/* Mobile App */}
        <div>
          <SectionTitle id="8">Mobile App Australia IPAY9 Casino</SectionTitle>
          <p className="mb-6">Take the casino in your pocket with the IPAY9 Casino mobile solutions. We offer a dedicated application for those who prefer a native experience and a perfectly optimized browser version.</p>
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            <div className="flex-1">
              <SubTitle>Android APK IPAY9</SubTitle>
              <BulletList items={[
                <><strong className="text-slate-700">Direct Download:</strong> Available as a secure APK directly from the IPAY9 official website.</>,
                <><strong className="text-slate-700">Push Notifications:</strong> Get instant alerts on new bonuses and match results.</>,
                <><strong className="text-slate-700">Biometric Login:</strong> Use your fingerprint or face ID for lightning-fast access.</>,
                <><strong className="text-slate-700">Data Saving:</strong> Optimized to use less mobile data than the browser version.</>,
                <><strong className="text-slate-700">Automatic Updates:</strong> The app stays current with the latest security patches and games.</>,
              ]} />
              <div className="mt-5">
                <SubTitle>iOS Browser IPAY9 Australia</SubTitle>
                <BulletList items={[
                  <><strong className="text-slate-700">No Download Required:</strong> Simply use Safari to visit IPAY9 online casino Australia.</>,
                  <><strong className="text-slate-700">Add to Home Screen:</strong> Create a shortcut for a &quot;quasi-app&quot; experience.</>,
                  <><strong className="text-slate-700">Retina Ready:</strong> Visuals are optimized for high-resolution Apple displays.</>,
                  <><strong className="text-slate-700">Touch Optimization:</strong> Smooth navigation designed specifically for iOS gestures.</>,
                  <><strong className="text-slate-700">Sync:</strong> Your account, balance, and history sync perfectly across all Apple devices.</>,
                ]} />
              </div>
            </div>
            <ContentImage
              src="https://static.readdy.ai/image/b513b5b4a847779ee65de1525456761b/02b028c04c24e4818767e47bd23a0dd8.png"
              alt="Mobile App Australia IPAY9 Casino"
              href={AFFILIATE_URL}
              btnLabel="Download app"
              source="main_download_app"
              mobileTall
            />
          </div>
        </div>

        {/* Promotions + Responsible Gaming */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h2 id="9" className="section-title text-slate-900 text-lg md:text-xl font-bold mb-4 flex items-center gap-3">
              <span className="w-1 h-6 rounded-full bg-gradient-to-b from-blue-500 to-blue-400 flex-shrink-0"></span>
              <strong>Promotions Calendar IPAY9 AU</strong>
            </h2>
            <div className="glass-card rounded-xl p-5 md:p-6">
              <p className="mb-3 text-slate-500">The excitement at IPAY9 AU never stops, thanks to our dynamic promotions calendar. We refresh our offers monthly to coincide with major sporting events, holidays, and new game launches.</p>
              <SubTitle>Monthly Tournaments IPAY9</SubTitle>
              <BulletList items={[
                <><strong className="text-slate-700">Slot Races:</strong> Climb the leaderboard by hitting the highest multipliers.</>,
                <><strong className="text-slate-700">Baccarat Challenges:</strong> Compete for the longest winning streak in the live casino.</>,
                <><strong className="text-slate-700">Seasonal Events:</strong> Special themes for Australia Day, Christmas, and New Year.</>,
                <><strong className="text-slate-700">Prize Tiers:</strong> Prizes distributed among the top 100 or 500 players.</>,
                <><strong className="text-slate-700">Entry:</strong> Most tournaments at IPAY9 Australia are free to enter for active players.</>,
              ]} />
            </div>
          </div>
          <div>
            <h2 id="10" className="section-title text-slate-900 text-lg md:text-xl font-bold mb-4 flex items-center gap-3">
              <span className="w-1 h-6 rounded-full bg-gradient-to-b from-blue-500 to-blue-400 flex-shrink-0"></span>
              <strong>Responsible Gaming Australia</strong>
            </h2>
            <div className="glass-card rounded-xl p-5 md:p-6">
              <p className="mb-3 text-slate-500">IPAY9 Casino Australia is committed to providing a fun yet safe environment. Our &quot;Responsible Gaming&quot; initiative provides Australian players with the resources and tools needed to stay in control.</p>
              <SubTitle>Tools at IPAY9 Casino AU</SubTitle>
              <BulletList items={[
                <><strong className="text-slate-700">Deposit Limits:</strong> Set daily, weekly, or monthly caps on how much you can deposit.</>,
                <><strong className="text-slate-700">Time-Outs:</strong> Take a temporary break ranging from 24 hours to 6 weeks.</>,
                <><strong className="text-slate-700">Self-Exclusion:</strong> A permanent option to close your account if you feel at risk.</>,
                <><strong className="text-slate-700">History Tracking:</strong> Easily view your total spend and time spent on the IPAY9 platform.</>,
                <><strong className="text-slate-700">Support Links:</strong> Direct access to professional help organizations like GamCare.</>,
              ]} />
            </div>
          </div>
        </div>

        {/* Game Providers */}
        <div>
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            <ContentImage
              src="https://static.readdy.ai/image/b513b5b4a847779ee65de1525456761b/89d6181a20bb1dc1845d57cfe3214b91.png"
              alt="Game Providers IPAY9 Casino Australia"
              href={AFFILIATE_URL}
              btnLabel="Choose your games"
              source="main_game_providers"
            />
            <div className="flex-1">
              <SectionTitle id="11">Game Providers IPAY9 Casino Australia</SectionTitle>
              <p className="mb-4">The quality of a casino is defined by its partners, and IPAY9 Casino Australia only works with the best. Our platform is powered by industry giants known for their innovation, reliability, and fair play certifications.</p>
              <SubTitle>Top Providers IPAY9 Casino</SubTitle>
              <BulletList items={[
                <><strong className="text-slate-700">JILI:</strong> Known for high-engagement slots, arcade titles, and popular fishing games with smooth mobile play.</>,
                <><strong className="text-slate-700">BNG:</strong> Modern slot catalogue with clean visuals, bonus-heavy mechanics, and frequent new releases.</>,
                <><strong className="text-slate-700">VPOWER:</strong> Fast-loading games optimized for mobile networks, featuring classic-style slots and quick-hit arcade fun.</>,
                <><strong className="text-slate-700">Live22:</strong> Live-style entertainment and casino game variety designed for immersive, real-time sessions.</>,
                <><strong className="text-slate-700">Pragmatic Play:</strong> A fan-favorite studio with iconic slots and feature-rich bonus rounds loved by Australian players.</>,
              ]} />
            </div>
          </div>
        </div>

        {/* Contact */}
        <div>
          <SectionTitle id="12">Contact IPAY9 Casino Australia</SectionTitle>
          <p className="mb-4">Customer satisfaction is our highest priority at IPAY9 Casino Australia. We offer 24/7 localized support to assist with any queries regarding your account, technical issues, or bonus clarifications.</p>
          <SubTitle>Live Chat IPAY9 AU</SubTitle>
          <BulletList items={[
            <><strong className="text-slate-700">Availability:</strong> Online 24 hours a day, 7 days a week, 365 days a year.</>,
            <><strong className="text-slate-700">Languages:</strong> Support available in English only.</>,
            <><strong className="text-slate-700">Response Time:</strong> Most queries are answered in under 60 seconds.</>,
            <><strong className="text-slate-700">Functionality:</strong> Upload screenshots directly to the chat for faster troubleshooting.</>,
            <><strong className="text-slate-700">Expertise:</strong> Our agents are experts in IPAY9 online casino Australia policies and games.</>,
          ]} />
        </div>

        {/* Screenshots */}
        <div>
          <SectionTitle id="13">Screenshots of IPAY9 App</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {[
              'https://static.readdy.ai/image/b513b5b4a847779ee65de1525456761b/fd0ecc06df16f11da018d292e70adb66.png',
              '/ipay9-referral-image.png',
              'https://static.readdy.ai/image/b513b5b4a847779ee65de1525456761b/d70c96123196ed3c24782f6194a0109d.png',
            ].map((src, i) => (
              <a
                key={i}
                href={AFFILIATE_URL}
                {...getAffiliateLinkProps(`main_screenshots_${i + 1}`, `Screenshots of IPAY9 App ${i + 1}`)}
                className="group mx-auto block w-full max-w-[min(320px,100%)] sm:mx-0 sm:max-w-none"
              >
                <div className="relative aspect-square w-full overflow-hidden rounded-2xl ring-1 ring-blue-200/45 shadow-sm transition-all duration-300 group-hover:ring-blue-400/50">
                  <img
                    alt={`Screenshots of IPAY9 App ${i + 1}`}
                    className="h-full w-full object-cover object-center"
                    src={src}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div>
          <SectionTitle id="14">FAQ Australia IPAY9 Online Casino</SectionTitle>
          <div>
            {faqItems.map((item, i) => (
              <div key={i} className="glass-card rounded-xl mb-3 overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-5 py-4 text-slate-800 text-sm font-semibold text-left cursor-pointer hover:bg-blue-50/30 transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="flex items-center gap-3">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-blue-50 text-blue-500 text-xs">
                      <i className={openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}></i>
                    </span>
                    {item.q}
                  </span>
                  <i className={`ri-arrow-down-s-line text-blue-500 text-lg flex-shrink-0 ml-2 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}></i>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-slate-500 text-sm leading-relaxed border-t border-blue-100/50 pt-3">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
